/** R9: the API sends ISO times with an offset, and every screen shows them in Asia/Kolkata. */
const ZONE = 'Asia/Kolkata';

type Lang = 'en' | 'mr' | 'hi';
const TAG: Record<Lang, string> = { en: 'en-IN', mr: 'mr-IN', hi: 'hi-IN' };
const ZONE_WORD: Record<Lang, string> = { en: 'IST', mr: 'भारतीय वेळ', hi: 'भारतीय समय' };

/**
 * The language dates are written in. The public pages set it with the page language, so a Marathi
 * page says "२० ऑक्टोबर, २०२६", never "20 October 2026". The broker app stays in English.
 */
let activeLang: Lang = 'en';
export function setDateLanguage(lang: Lang): void {
  activeLang = lang;
}

const dateOnlyFor = (lang: Lang) =>
  new Intl.DateTimeFormat(TAG[lang], {
    timeZone: ZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
const timeOnlyFor = (lang: Lang) =>
  new Intl.DateTimeFormat(TAG[lang], {
    timeZone: ZONE,
    hour: 'numeric',
    minute: '2-digit',
    ...(lang === 'en' ? { hour12: true } : { hourCycle: 'h23' as const }),
  });
const dateOnly = { format: (d: Date) => dateOnlyFor(activeLang).format(d) };

/** `18 October 2026, 11:00 am IST`. A Marathi or Hindi page gets its own words and a 24 hour clock. */
export function formatIst(iso: string): string {
  const d = new Date(iso);
  return activeLang === 'en'
    ? `${dateOnlyFor('en').format(d)}, ${timeOnlyFor('en').format(d)} ${ZONE_WORD.en}`
    : `${dateOnlyFor(activeLang).format(d)}, ${timeOnlyFor(activeLang).format(d)} ${ZONE_WORD[activeLang]}`;
}

/** `18 October 2026`, the calendar date in Asia/Kolkata. */
export function formatIstDate(iso: string): string {
  return dateOnly.format(new Date(iso));
}

const longDay = new Intl.DateTimeFormat('en-IN', {
  timeZone: ZONE,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/** `Saturday, 18 October`, today in Asia/Kolkata. */
export function formatIstToday(now: Date = new Date()): string {
  return longDay.format(now);
}

const dayMonthFor = (lang: Lang) =>
  new Intl.DateTimeFormat(TAG[lang], { timeZone: ZONE, day: 'numeric', month: 'short' });

/** A calendar date (`2026-10-31`) as a date, or null when it is empty or not a date. */
function calendarDate(date: string | null | undefined): Date | null {
  if (date === null || date === undefined || !/^\d{4}-\d{2}-\d{2}/.test(date)) return null;
  const parsed = new Date(`${date.slice(0, 10)}T00:00:00+05:30`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** `31 Oct` for a calendar date (`2026-10-31`). Empty input gives an empty string, never an error. */
export function formatDayMonth(date: string | null | undefined): string {
  const parsed = calendarDate(date);
  return parsed === null ? '' : dayMonthFor(activeLang).format(parsed);
}

/** `31 October 2026` for a calendar date (`2026-10-31`). Empty input gives an empty string. */
export function formatCalendarDate(date: string | null | undefined): string {
  const parsed = calendarDate(date);
  return parsed === null ? '' : dateOnly.format(parsed);
}

const monthYearFor = (lang: Lang) =>
  new Intl.DateTimeFormat(TAG[lang], { timeZone: ZONE, month: 'short', year: 'numeric' });

/** `Oct 2026` for a month key (`2026-10`). Anything else gives an empty string. */
export function formatMonth(key: string | null | undefined): string {
  if (key === null || key === undefined || !/^\d{4}-\d{2}$/.test(key)) return '';
  const parsed = new Date(`${key}-01T00:00:00+05:30`);
  return Number.isNaN(parsed.getTime()) ? '' : monthYearFor(activeLang).format(parsed);
}
